import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight, ArrowUpRight, BadgeCheck, GraduationCap, HeartHandshake,
  Users, MapPin, Quote,
} from "lucide-react";
import api from "../lib/api";
import { Reveal, MaskedLines, FadeIn, Marquee, SectionHeading, EASE } from "../components/Motion";
import CourseCard from "../components/CourseCard";
import { Button } from "../components/ui/button";

const HERO_IMG = "https://images.unsplash.com/photo-1542810634-71277d95dcbb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHw0fHxpbmRpYW4lMjB0ZWFjaGVyJTIwY2xhc3Nyb29tJTIwa2lkc3xlbnwwfHx8fDE3ODc0NzkzNzR8MA&ixlib=rb-4.1.0&q=85";

const MARQUEE_ITEMS = [
  "Govt. Recognized Framework",
  "NCTE Guideline Aligned",
  "100% Placement Assistance",
  "Certified NTT · NST · DNTT · PTT",
  "IELTS Band 7+ Coaching",
  "Mandya, Karnataka",
];

const PILLARS = [
  {
    icon: HeartHandshake,
    title: "Faith-Led Service",
    text: "Guided by Christian values of service and compassion, every program exists to uplift the learner and the community.",
  },
  {
    icon: GraduationCap,
    title: "Pedagogical Excellence",
    text: "Modern, child-centric teaching methodologies delivered by experienced educators and counsellors.",
  },
  {
    icon: Users,
    title: "Community First",
    text: "Vocational programs and concessions that open doors for rural youth, women and underprivileged families.",
  },
];

const CHAPTERS = [
  {
    num: "01",
    title: "The Divine Calling to Teach",
    text: "We believe teaching is a calling, not merely a career. Our nursery and primary teacher training forms educators who lead classrooms with patience, purpose and love.",
    img: HERO_IMG,
  },
  {
    num: "02",
    title: "Child-Centric Pedagogies",
    text: "Play-way learning, Montessori-inspired methods, storytelling and activity-based classrooms — our trainees practice what modern early education demands.",
    img: "https://images.pexels.com/photos/8422132/pexels-photo-8422132.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    num: "03",
    title: "Rural & Semi-Urban Empowerment",
    text: "Rooted in Mandya, we carry skills to the communities that need them most — vocational training and livelihood programs for sustainable independence.",
    img: "https://images.unsplash.com/flagged/photo-1574097656146-0b43b7660cb6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHxpbmRpYW4lMjB0ZWFjaGVyJTIwY2xhc3Nyb29tJTIwa2lkc3xlbnwwfHx8fDE3ODc0NzkzNzR8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    num: "04",
    title: "Global Language Proficiency",
    text: "From first words in English to IELTS band 7+, we walk with learners until the language stops being a barrier and becomes a bridge.",
    img: "https://images.pexels.com/photos/5676663/pexels-photo-5676663.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
];

const STEPS = [
  { num: "01", title: "Enquire", text: "Reach us by form, call or WhatsApp — tell us which course calls you." },
  { num: "02", title: "Counsel", text: "Meet our counsellor, understand batches, fees and concessions available to you." },
  { num: "03", title: "Enrol", text: "Complete admission, join your batch and begin your formation as an educator." },
];

export default function Home() {
  const [courses, setCourses] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);

  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    api.get("/courses").then((r) => setCourses(r.data.slice(0, 4))).catch(() => {});
    api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* ============ KINETIC HERO ============ */}
      <section ref={heroRef} className="relative overflow-hidden pt-32 lg:pt-40" data-testid="hero-section">
        <div className="pointer-events-none absolute -right-40 -top-40 h-[520px] w-[520px] rounded-full bg-gold/15 blur-3xl" />
        <div className="pointer-events-none absolute -left-52 top-64 h-[420px] w-[420px] rounded-full bg-terracotta/10 blur-3xl" />

        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-16 px-4 pb-20 sm:px-6 lg:grid-cols-12 lg:px-8 lg:pb-28">
          <div className="lg:col-span-7">
            <FadeIn>
              <p data-testid="hero-kicker" className="inline-flex items-center gap-2 rounded-full border border-forest/15 bg-white/70 px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.22em] text-forest">
                <MapPin className="h-3 w-3 text-terracotta" /> St. Mary's Charitable Trust · Mandya
              </p>
            </FadeIn>

            <h1
              data-testid="hero-heading"
              className="mt-7 font-serif text-5xl font-bold leading-[1.04] tracking-tight text-forest-deep sm:text-6xl lg:text-7xl"
            >
              <MaskedLines
                lines={[
                  "Forming Teachers.",
                  <span key="l2" className="italic text-terracotta">Transforming</span>,
                  "Generations.",
                ]}
              />
            </h1>

            <FadeIn delay={0.65}>
              <p className="mt-7 max-w-xl text-base leading-relaxed text-stone-600 sm:text-lg">
                A premier Christian charitable trust offering certified NTT, NST, DNTT and PTT teacher training,
                Spoken English and IELTS coaching — with 100% placement assistance and a heart for community.
              </p>
            </FadeIn>

            <FadeIn delay={0.8}>
              <div className="mt-9 flex flex-wrap items-center gap-4">
                <Button asChild size="lg" data-testid="hero-explore-courses-button" className="rounded-full bg-forest px-8 font-display text-cream hover:bg-forest-deep">
                  <Link to="/courses">Explore Courses <ArrowRight className="ml-2 h-4 w-4" /></Link>
                </Button>
                <Button asChild size="lg" variant="outline" data-testid="hero-contact-button" className="rounded-full border-forest/30 px-8 font-display text-forest hover:bg-forest hover:text-cream">
                  <Link to="/contact">Contact Us</Link>
                </Button>
              </div>
            </FadeIn>

            <FadeIn delay={0.95}>
              <dl className="mt-12 flex flex-wrap gap-x-12 gap-y-6">
                {[
                  ["2025", "Established"],
                  ["7", "Certified Programs"],
                  ["100%", "Placement Assistance"],
                ].map(([stat, label]) => (
                  <div key={label} data-testid={`hero-stat-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                    <dt className="font-serif text-4xl font-bold text-forest sm:text-5xl">{stat}</dt>
                    <dd className="mt-1 font-mono text-[11px] uppercase tracking-[0.18em] text-stone-500">{label}</dd>
                  </div>
                ))}
              </dl>
            </FadeIn>
          </div>

          <div className="relative lg:col-span-5">
            <motion.div
              initial={{ clipPath: "inset(100% 0 0 0)" }}
              animate={{ clipPath: "inset(0% 0 0 0)" }}
              transition={{ duration: 1.3, delay: 0.35, ease: EASE }}
              className="relative overflow-hidden rounded-b-[2rem] rounded-t-[16rem] border-[10px] border-white shadow-2xl shadow-forest/20"
              data-testid="hero-image-frame"
            >
              <motion.img
                src={HERO_IMG}
                alt="Children learning in a classroom supported by St Mary's Charitable Trust"
                style={{ y: imgY }}
                className="h-[420px] w-full scale-125 object-cover sm:h-[520px]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-forest-deep/30 to-transparent" />
            </motion.div>

            <FadeIn delay={1.1} className="absolute -left-6 top-16 sm:-left-12">
              <div className="animate-floaty rounded-2xl border border-forest/10 bg-white/95 px-5 py-4 shadow-xl shadow-forest/10 backdrop-blur" data-testid="hero-badge-placement">
                <div className="flex items-center gap-3">
                  <BadgeCheck className="h-8 w-8 text-terracotta" />
                  <div>
                    <p className="font-serif text-lg font-bold leading-none text-forest-deep">100%</p>
                    <p className="mt-1 text-[11px] uppercase tracking-[0.14em] text-stone-500">Placement Support</p>
                  </div>
                </div>
              </div>
            </FadeIn>

            <FadeIn delay={1.25} className="absolute -bottom-6 right-2 sm:-right-4">
              <div className="rounded-2xl bg-forest px-5 py-4 shadow-xl shadow-forest/30" data-testid="hero-badge-est">
                <p className="font-serif text-lg font-bold leading-none text-gold">Est. 2025</p>
                <p className="mt-1 text-[11px] uppercase tracking-[0.14em] text-cream/70">Mandya · Karnataka</p>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ============ EDITORIAL MARQUEE ============ */}
      <Marquee items={MARQUEE_ITEMS} />

      {/* ============ PILLARS ============ */}
      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-32" data-testid="pillars-section">
        <SectionHeading kicker="Our Foundation" title={<>Built on faith,<br />grown through education.</>} />
        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3">
          {PILLARS.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.12}>
              <div
                data-testid={`pillar-card-${p.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="group h-full rounded-3xl border border-forest/10 bg-white p-8 transition-all duration-500 hover:-translate-y-2 hover:border-terracotta/30 hover:shadow-xl hover:shadow-forest/5"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-linen text-terracotta transition-colors duration-500 group-hover:bg-terracotta group-hover:text-white">
                  <p.icon className="h-6 w-6" />
                </span>
                <h3 className="mt-6 font-serif text-2xl font-semibold text-forest-deep">{p.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-stone-600">{p.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ============ FEATURED COURSES ============ */}
      <section className="bg-linen/70 py-24 lg:py-32" data-testid="featured-courses-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <SectionHeading kicker="Our Courses" title={<>Professional training,<br />personal formation.</>} />
            <Reveal delay={0.2}>
              <Button asChild variant="outline" data-testid="view-all-courses-button" className="rounded-full border-forest/30 font-display text-forest hover:bg-forest hover:text-cream">
                <Link to="/courses">All 7 Programs <ArrowUpRight className="ml-1 h-4 w-4" /></Link>
              </Button>
            </Reveal>
          </div>
          <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {courses.map((c, i) => (
              <Reveal key={c.id} delay={i * 0.1}>
                <CourseCard course={c} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ MANIFESTO CHAPTERS ============ */}
      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-32" data-testid="manifesto-section">
        <SectionHeading kicker="The Manifesto" title="Four chapters of our calling." />
        <div className="mt-16 space-y-20">
          {CHAPTERS.map((ch, i) => (
            <div key={ch.num} className={`grid grid-cols-1 items-center gap-10 lg:grid-cols-12 ${i % 2 ? "" : ""}`}>
              <Reveal className={`lg:col-span-7 ${i % 2 ? "lg:order-2" : ""}`}>
                <div className="group overflow-hidden rounded-3xl">
                  <img
                    src={ch.img}
                    alt={ch.title}
                    loading="lazy"
                    className="h-72 w-full object-cover transition-transform duration-700 group-hover:scale-105 sm:h-96"
                  />
                </div>
              </Reveal>
              <Reveal delay={0.15} className={`lg:col-span-5 ${i % 2 ? "lg:order-1" : ""}`}>
                <span className="font-serif text-7xl font-bold text-outline sm:text-8xl" data-testid={`chapter-number-${ch.num}`}>{ch.num}</span>
                <h3 className="mt-4 font-serif text-3xl font-semibold text-forest-deep">{ch.title}</h3>
                <p className="mt-4 max-w-md text-base leading-relaxed text-stone-600">{ch.text}</p>
              </Reveal>
            </div>
          ))}
        </div>
      </section>

      {/* ============ TESTIMONIALS ============ */}
      {testimonials.length > 0 && (
        <section className="grain-overlay relative bg-forest py-24 text-cream lg:py-32" data-testid="testimonials-section">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Reveal>
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-gold">Voices of Our Students</p>
              <h2 className="mt-4 font-serif text-3xl font-semibold leading-tight sm:text-4xl lg:text-5xl">
                Lives changed,<br />in their own words.
              </h2>
            </Reveal>
            <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3">
              {testimonials.map((t, i) => (
                <Reveal key={t.id} delay={i * 0.12}>
                  <figure
                    data-testid={`testimonial-card-${i}`}
                    className="flex h-full flex-col rounded-3xl border border-cream/10 bg-cream/[0.04] p-8 backdrop-blur transition-colors duration-500 hover:border-gold/40"
                  >
                    <Quote className="h-7 w-7 text-gold" fill="currentColor" />
                    <blockquote className="mt-5 flex-1 font-serif text-xl italic leading-relaxed text-cream/90">{t.quote}</blockquote>
                    <figcaption className="mt-6 flex items-center gap-3 border-t border-cream/10 pt-4">
                      {t.image ? (
                        <img src={t.image} alt={t.name} className="h-11 w-11 rounded-full border border-gold/40 object-cover" data-testid={`testimonial-avatar-${i}`} />
                      ) : (
                        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-gold/20 font-serif text-lg font-bold text-gold" data-testid={`testimonial-avatar-${i}`}>
                          {t.name?.[0] || "S"}
                        </span>
                      )}
                      <div>
                        <p className="font-display font-semibold">{t.name}</p>
                        <p className="text-xs uppercase tracking-[0.14em] text-cream/50">{t.role}</p>
                      </div>
                    </figcaption>
                  </figure>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ============ ADMISSION PATHWAY ============ */}
      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-32" data-testid="pathway-section">
        <SectionHeading kicker="Admissions Open 2026–27" title="Your path in three steps." />
        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3">
          {STEPS.map((s, i) => (
            <Reveal key={s.num} delay={i * 0.12}>
              <div data-testid={`pathway-step-${s.num}`} className="relative h-full rounded-3xl border border-forest/10 bg-white p-8">
                <span className="font-serif text-6xl font-bold text-terracotta/25">{s.num}</span>
                <h3 className="mt-4 font-serif text-2xl font-semibold text-forest-deep">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-stone-600">{s.text}</p>
                {i < 2 && <ArrowRight className="absolute -right-3 top-1/2 hidden h-6 w-6 -translate-y-1/2 text-terracotta md:block" />}
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.3} className="mt-12 text-center">
          <Button asChild size="lg" data-testid="pathway-apply-button" className="rounded-full bg-terracotta px-10 font-display text-white hover:bg-terracotta-dark">
            <Link to="/admissions">Begin Your Application <ArrowRight className="ml-2 h-4 w-4" /></Link>
          </Button>
        </Reveal>
      </section>

      {/* ============ CTA RIBBON ============ */}
      <section className="px-4 pb-24 sm:px-6 lg:px-8" data-testid="cta-section">
        <Reveal>
          <div className="grain-overlay relative mx-auto max-w-7xl overflow-hidden rounded-[2.5rem] bg-forest-deep px-8 py-20 text-center text-cream sm:px-16">
            <div className="pointer-events-none absolute -left-24 -top-24 h-72 w-72 rounded-full bg-gold/10 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-24 -right-24 h-72 w-72 rounded-full bg-terracotta/20 blur-3xl" />
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-gold">Franchise & Enrolment</p>
            <h2 className="mx-auto mt-5 max-w-3xl font-serif text-3xl font-semibold leading-tight sm:text-4xl lg:text-5xl">
              Begin your teaching journey — or open your own centre with us.
            </h2>
            <p className="mx-auto mt-5 max-w-xl text-sm leading-relaxed text-cream/70">
              Join a proven model of education with complete support. Call us today and let's talk about your future.
            </p>
            <div className="mt-9 flex flex-wrap justify-center gap-4">
              <Button asChild size="lg" data-testid="cta-call-button" className="rounded-full bg-gold px-8 font-display text-forest-deep hover:bg-cream">
                <a href="tel:+917975094721">Call +91 79750 94721</a>
              </Button>
              <Button asChild size="lg" variant="outline" data-testid="cta-enquire-button" className="rounded-full border-cream/30 px-8 font-display text-cream hover:bg-cream hover:text-forest-deep">
                <Link to="/contact">Send an Enquiry</Link>
              </Button>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
